// Wealth rankboard overlay (opened at the bank's rankboard). Shows the top players ranked by
// combined wealth (carried cash + bank). The server is authoritative: opening requests the board,
// and the anonymity checkbox sends a toggle — when anonymous, a player's name shows blank for
// everyone (including themselves).

import { injectTheme } from "./theme.js";
import type { LeaderboardEntry } from "@ghostland/shared";

export interface RankboardActions {
  request: () => void;
  setAnonymous: (anonymous: boolean) => void;
}
let actions: RankboardActions | null = null;
export function setRankboardActions(a: RankboardActions): void { actions = a; }

const STYLE_ID = "ghostland-rank-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-rank-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(0,0,0,.7);
  color: #e8e8e8;
}
#gl-rank-overlay, #gl-rank-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-rank-overlay.open { display: flex; }
#gl-rank-card {
  width: 320px; padding: 20px 18px 16px; background: #000;
  border: 2px solid #f4f4f4; border-radius: 0; image-rendering: pixelated;
}
#gl-rank-card h1 { margin: 0 0 2px; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
#gl-rank-card h1::after { content: "_"; animation: gl-rank-blink 1s steps(1) infinite; }
#gl-rank-card .hint { font-size: 7px; color: #7a7a7a; letter-spacing: 1px; margin-bottom: 14px; }
#gl-rank-list { display: flex; flex-direction: column; }
.gl-rank-row {
  display: flex; align-items: center; gap: 8px; padding: 7px 4px; font-size: 9px;
  border-bottom: 1px solid #1c1c1c;
  animation: gl-rank-pop .22s steps(3) backwards;
}
.gl-rank-row .rk { width: 22px; color: #6a6a6a; }
.gl-rank-row .nm { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #d8d8d8; }
.gl-rank-row .nm.anon { color: #5a5a5a; }
.gl-rank-row .wl { color: #f4f4f4; }
.gl-rank-row.top1 .rk, .gl-rank-row.top1 .nm { color: #fff; }
.gl-rank-row:nth-child(1){animation-delay:.02s} .gl-rank-row:nth-child(2){animation-delay:.05s}
.gl-rank-row:nth-child(3){animation-delay:.08s} .gl-rank-row:nth-child(4){animation-delay:.11s}
.gl-rank-row:nth-child(5){animation-delay:.14s} .gl-rank-row:nth-child(6){animation-delay:.17s}
.gl-rank-row:nth-child(7){animation-delay:.20s} .gl-rank-row:nth-child(8){animation-delay:.23s}
.gl-rank-row:nth-child(9){animation-delay:.26s} .gl-rank-row:nth-child(10){animation-delay:.29s}
#gl-rank-empty { font-size: 8px; color: #6a6a6a; padding: 18px 0; text-align: center; letter-spacing: 1px; }
#gl-rank-anon {
  display: flex; align-items: center; gap: 8px; margin-top: 14px; padding-top: 12px;
  border-top: 1px solid #1c1c1c; font-size: 8px; color: #8a8a8a; letter-spacing: 1px; cursor: pointer;
}
#gl-rank-anon input {
  -webkit-appearance: none; appearance: none; width: 13px; height: 13px; margin: 0; cursor: pointer;
  background: #000; border: 2px solid #6a6a6a; image-rendering: pixelated;
}
#gl-rank-anon input:checked { background: #f4f4f4; box-shadow: inset 0 0 0 2px #000; }
#gl-rank-close {
  margin-top: 12px; width: 100%; padding: 7px 0; background: #000; image-rendering: pixelated;
  border: 2px solid #6a6a6a; border-radius: 0; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px;
}
#gl-rank-close:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
@keyframes gl-rank-pop { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: none; } }
@keyframes gl-rank-blink { 0%,49% { opacity: 1; } 50%,100% { opacity: 0; } }
`;

let overlay: HTMLDivElement | null = null;
let listEl: HTMLDivElement | null = null;
let anonBox: HTMLInputElement | null = null;

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-rank-overlay";
  overlay.innerHTML = `
    <div id="gl-rank-card">
      <h1>WEALTH RANKINGS</h1>
      <div class="hint">richest in Ghost Land — cash + bank</div>
      <div id="gl-rank-list"><div id="gl-rank-empty">Loading…</div></div>
      <label id="gl-rank-anon"><input type="checkbox" /> <span>Stay anonymous (hide my name)</span></label>
      <button id="gl-rank-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  listEl = overlay.querySelector("#gl-rank-list");
  anonBox = overlay.querySelector("#gl-rank-anon input");
  anonBox!.addEventListener("change", () => actions?.setAnonymous(anonBox!.checked));
  overlay.querySelector("#gl-rank-close")!.addEventListener("click", closeRankboard);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeRankboard(); });
}

/** Populate the board from a server Leaderboard message. */
export function renderLeaderboard(entries: LeaderboardEntry[], youAnonymous: boolean): void {
  ensure();
  if (anonBox) anonBox.checked = youAnonymous;
  if (!listEl) return;
  if (!entries.length) { listEl.innerHTML = `<div id="gl-rank-empty">No players ranked yet.</div>`; return; }
  listEl.replaceChildren(...entries.map((e, i) => {
    const row = document.createElement("div");
    row.className = "gl-rank-row" + (i === 0 ? " top1" : "");
    const blank = !e.name;
    row.innerHTML =
      `<span class="rk">#${i + 1}</span>` +
      `<span class="nm${blank ? " anon" : ""}">${blank ? "— anonymous —" : escapeHtml(e.name)}</span>` +
      `<span class="wl">$${e.wealth.toLocaleString()}</span>`;
    return row;
  }));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

export function openRankboard(): void {
  ensure();
  overlay!.classList.add("open");
  actions?.request(); // pull the latest board from the server
}

export function closeRankboard(): void {
  overlay?.classList.remove("open");
}

export function isRankboardOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
